import {
  ContentCompletionByMonth,
  InsightsData,
  ModuleInsight,
  ProductVisibility,
} from "@coaching-culture/types";
import {
  BarChart,
  Box,
  Button,
  DonutGraph,
  EmptyState,
  Flex,
  IconButton,
  Modal,
  Panel,
  PanelHeader,
  StatTile,
  Text,
  sanitizeHtml,
} from "@coaching-culture/ui";
import { useOrg } from "auth/OrgProvider";
import { ActivityMatrix } from "components/ActivityMatrix";
import StarRating from "components/StarRating";
import { useProductVisibility } from "hooks/useProductVisibility";
import { useCallback, useMemo, useRef, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import {
  FaChartBar,
  FaQuestion,
  FaRegWindowClose,
  FaRegWindowMaximize,
} from "react-icons/fa";
import styled, { useTheme } from "styled-components";
import {
  grid,
  GridProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
} from "styled-system";
import { CompletionsWidget } from "./CompletionsWidget";

const WidgetGrid = styled.div<GridProps & SpaceProps & LayoutProps>`
  display: grid;
  justify-content: center;
  ${grid};
  ${layout};
  ${space};
`;

const ModuleIcon = styled.img`
  width: 32px;
  height: 32px;
`;

const StatGrid = styled.div<{ min: number }>`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(
    auto-fit,
    minmax(${(props) => props.min}px, 1fr)
  );
`;

/* Five counts in one row: too narrow for StatTiles inside a module card. */
const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const StatCell = styled.div`
  padding: 8px 4px;
  text-align: center;

  &:not(:last-child) {
    border-right: 1px solid ${(props) => props.theme.colors.border};
  }
`;

const ModuleStat = ({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) => (
  <StatCell>
    <Text
      fontSize="section"
      fontWeight={700}
      color="black"
      lineHeight={1.3}
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {value ?? 0}
    </Text>
    <Text fontSize="caption" color="textMuted" lineHeight={1.3}>
      {label}
    </Text>
  </StatCell>
);

const NoDataContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoData = () => (
  <NoDataContainer>
    <EmptyState icon={FaChartBar} compact>
      No data yet.
    </EmptyState>
  </NoDataContainer>
);

const PopOver = styled(Panel)`
  position: absolute;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 3px;
  padding: 6px 12px;
  z-index: 1000;
`;

const ModuleBarChart = ({
  module,
  color,
}: {
  module: ModuleInsight;
  color: string;
}) => {
  const popoverRef = useRef<HTMLDivElement>(null!);
  const [mouseOver, setMouseOver] = useState<{
    label: string;
    value: number;
  } | null>(null);

  const data = useMemo(
    () =>
      module.data.map((x) => ({
        label: x.name,
        streams: Array.isArray(x.count)
          ? (x.count.map((y) => ({ label: y.label, value: y.value })) as {
              label: string;
              value: number;
            }[])
          : [{ label: "", value: x.count }],
      })),
    [module],
  );

  const onMouseOver = useCallback((dataPoint) => {
    popoverRef.current.style.opacity = dataPoint == null ? "0" : "1";
    if (dataPoint != null) {
      setMouseOver({
        label: dataPoint.x,
        value: dataPoint.y,
      });
    }
  }, []);

  const onMouseMove = useCallback((e) => {
    var rect = e.currentTarget.getBoundingClientRect();
    var x = e.clientX - rect.left; //x position within the element.
    var y = e.clientY - rect.top; //y position within the element.
    popoverRef.current.style.left = x + "px";
    popoverRef.current.style.top = `${y - 40}px`;
  }, []);

  return (
    <Box
      onMouseMove={onMouseMove}
      style={{
        position: "relative",
      }}
    >
      <BarChart
        xAxis={module.xAxis}
        yAxis={module.yAxis}
        streamLabels={[""]}
        onValueMouseOver={onMouseOver}
        color={color}
        data={data}
      />
      <PopOver ref={popoverRef} p={1} color="none" boxShadow={2}>
        <Text color="body" fontWeight={600} style={{ whiteSpace: "nowrap" }}>
          {mouseOver?.label}: {mouseOver?.value}
        </Text>
      </PopOver>
    </Box>
  );
};

const PieChart = ({ module }: { module: ModuleInsight }) => {
  return (
    <DonutGraph
      values={module.data.map((x) => ({
        label: x.name,
        value: x.count,
      }))}
    />
  );
};

const hasNoData = (mod: ModuleInsight) => {
  if (mod.data.every((x) => x.count === 0)) {
    return -1;
  }
  return 1;
};

const Insights = ({
  data,
  completions,
}: {
  data: InsightsData;
  completions: ContentCompletionByMonth[] | null;
}) => {
  const hasMindset =
    useProductVisibility("mindset") === ProductVisibility.Enabled;
  const insightsRef = useRef<HTMLDivElement>(null!);
  const [showHelp, setShowHelp] = useState({ name: "", text: "" });
  const [selectedModule, setSelectedModule] = useState("");
  const [org] = useOrg();
  const theme = useTheme();

  return (
    <>
      {showHelp.text !== "" && (
        <Modal
          width={"500px"}
          onClose={() => setShowHelp({ name: "", text: "" })}
          showCloseButton
        >
          <Box p={3}>
            <PanelHeader mb={2}>
              <Text fontSize="section" fontWeight={600} color="black">
                {showHelp.name}
              </Text>
            </PanelHeader>
            {ReactHtmlParser(sanitizeHtml(showHelp.text))}
            <Flex mt={2} justifyContent={"flex-end"}>
              <Button
                type="button"
                onClick={() => setShowHelp({ name: "", text: "" })}
                mr={2}
              >
                Cancel
              </Button>
            </Flex>
          </Box>
        </Modal>
      )}
      <Text fontSize="section" fontWeight={600} color="black" mb={3}>
        Engagement
      </Text>
      <div ref={insightsRef}>
        <StatGrid min={180} style={{ marginBottom: 16 }}>
          <StatTile label="Total users" value={data.totalUsers} />
          <StatTile label="Total module attempts" value={data.moduleAttempts} />
          <StatTile
            label="Active users (7 days)"
            value={data.activeUsersWeek}
          />
          <StatTile
            label="Active users (30 days)"
            value={data.activeUsersMonth}
          />
        </StatGrid>
        <Panel
          mb={5}
          style={{ pageBreakInside: "avoid", pageBreakAfter: "always" }}
        >
          <PanelHeader>
            <Text fontSize="title" fontWeight={600} color="black">
              Activity by hour (last 30 days)
            </Text>
          </PanelHeader>
          <Box p={3}>
            <ActivityMatrix data={data.activity} />
          </Box>
        </Panel>
        {completions && <CompletionsWidget completionData={completions} />}
        {hasMindset && (
          <>
            <Text fontSize="section" fontWeight={600} color="black" mb={3}>
              Modules
            </Text>
            {data.modules.length === 0 ? (
              <EmptyState icon={FaChartBar}>No modules available.</EmptyState>
            ) : (
              <WidgetGrid
                gridGap={[3, 3]}
                gridTemplateColumns={"repeat(auto-fit, minmax(500px, 1fr))"}
                display={selectedModule !== "" ? "flex" : ["block", "grid"]}
              >
                {data.modules
                  .sort((a, b) => {
                    return (
                      hasNoData(b) - hasNoData(a) ||
                      a.name.localeCompare(b.name)
                    );
                  })
                  .map((x) => (
                    <Panel
                      width={selectedModule === x.id ? "75%" : ""}
                      display={
                        selectedModule !== "" && selectedModule !== x.id
                          ? "none"
                          : ""
                      }
                      p={3}
                      key={x.id}
                      style={{
                        position: "relative",
                        pageBreakAfter: "always",
                      }}
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Flex
                          justifyContent={"flex-start"}
                          alignItems={"center"}
                        >
                          <ModuleIcon
                            src={
                              process.env.REACT_APP_STATIC_ROOT + "/" + x.icon
                            }
                          />
                          <Text
                            ml={2}
                            fontSize="title"
                            fontWeight={600}
                            color="black"
                          >
                            {x.name}
                          </Text>
                        </Flex>
                        <Flex
                          justifyContent={"flex-end"}
                          alignItems={"center-end"}
                        >
                          {x.reportHelp !== "" && (
                            <IconButton
                              mr={2}
                              color="body"
                              aria-label="Help"
                              icon={FaQuestion}
                              onClick={() =>
                                setShowHelp({
                                  name: x.name,
                                  text: x.reportHelp,
                                })
                              }
                            />
                          )}
                          {selectedModule === x.id ? (
                            <IconButton
                              mr={2}
                              color="body"
                              aria-label="Close fullscreen"
                              icon={FaRegWindowClose}
                              onClick={() => setSelectedModule("")}
                            />
                          ) : (
                            <IconButton
                              mr={2}
                              color="body"
                              aria-label="Fullscreen"
                              icon={FaRegWindowMaximize}
                              onClick={() => setSelectedModule(x.id)}
                            />
                          )}
                        </Flex>
                      </Flex>
                      <StatStrip>
                        <ModuleStat label="Started" value={x.started} />
                        <ModuleStat
                          label="Assessment completed"
                          value={x.formCompleted}
                        />
                        <ModuleStat
                          label="Programme completed"
                          value={x.challengeStarted}
                        />
                        <ModuleStat
                          label="Challenge completed"
                          value={x.challengeCompleted}
                        />
                        <ModuleStat label="Completed" value={x.completed} />
                      </StatStrip>
                      <Box style={{ position: "relative" }}>
                        {x.graphType === "bar" ? (
                          <ModuleBarChart
                            module={x}
                            color={theme.colors.primary}
                          />
                        ) : x.graphType === "pie" ? (
                          <PieChart module={x} />
                        ) : null}
                        {x.data.every((x) => x.count === 0) && <NoData />}
                      </Box>
                      {org.collectRatings && (
                        <Flex
                          borderTop={1}
                          pt={2}
                          mt={2}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Text fontSize="small" color="textMuted">
                            User rating
                          </Text>
                          {x.averageRating == null ? (
                            <Text fontSize="small" color="textMuted">
                              No ratings yet
                            </Text>
                          ) : (
                            <Flex alignItems="center">
                              <StarRating
                                value={x.averageRating}
                                size={"1.25em"}
                              />
                              <Text ml={1} fontWeight={600}>
                                {x.averageRating.toFixed(1)}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      )}
                    </Panel>
                  ))}
              </WidgetGrid>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Insights;
